// src/pages/AcceptOrgInvitePage.jsx
import { useEffect, useState } from 'react'
import { Loader2, CheckCircle2, AlertTriangle } from 'lucide-react'
import { fetchWithAuth } from '../api/fetchWithAuth'
import { acceptInviteErrorMessage } from './acceptInviteError'

/**
 * Accept-org-invite page. Mounted at /accept-org-invite/$inviteId via
 * acceptOrgInviteRoute in src/router.jsx.
 *
 * Note on StrictMode: we deliberately DON'T guard against the double-mount.
 * The backend accept endpoint is idempotent — the second call returns
 * `already_accepted` which we treat as success. The previous attempt to
 * dedupe via a useRef left the UI stuck on the spinner because the first
 * mount's cleanup set cancelled=true and blocked setState, while the
 * second mount skipped the fetch entirely due to the ref guard. Net
 * result: POST ran, succeeded, but no UI update ever happened.
 *
 * Correct pattern: always run the fetch, use `cancelled` only to prevent
 * setState on an unmounted component.
 */
export default function AcceptOrgInvitePage({ inviteId, navigate }) {
  const [status, setStatus] = useState('accepting')
  const [message, setMessage] = useState(null)

  useEffect(() => {
    let cancelled = false

    async function accept() {
      try {
        const res = await fetchWithAuth(
          `/api/v1/org-invites/${inviteId}/accept`,
          { method: 'POST' }
        )

        // If this component instance has been unmounted, don't touch state.
        // The next mount (in StrictMode) will run its own fetch and succeed
        // — backend returns 'already_accepted' which is fine.
        if (cancelled) return

        if (res.ok) {
          setStatus('success')
          setTimeout(() => {
            if (!cancelled) navigate({ to: '/' })
          }, 1500)
          return
        }

        let body = null
        try { body = await res.json() } catch { /* not JSON */ }
        const errMsg = acceptInviteErrorMessage(res.status, body, 'org')

        setStatus('error')
        setMessage(errMsg)
      } catch (e) {
        if (cancelled) return
        console.error('[accept-invite] failed', e)
        setStatus('error')
        setMessage('Network error. Check your connection and try again.')
      }
    }

    accept()
    return () => { cancelled = true }
  }, [inviteId, navigate])

  return (
    <div className="min-h-screen flex items-center justify-center bg-bg p-6">
      <div className="max-w-md w-full bg-bg-soft border border-bg-border rounded-lg p-8 text-center space-y-4">
        {status === 'accepting' && (
          <>
            <Loader2 className="mx-auto animate-spin text-gray-400" size={32} />
            <h1 className="text-lg font-semibold text-gray-200">
              Accepting invitation…
            </h1>
            <p className="text-xs text-gray-400">
              One moment while we add you to the organization.
            </p>
          </>
        )}

        {status === 'success' && (
          <>
            <CheckCircle2 className="mx-auto text-green-400" size={32} />
            <h1 className="text-lg font-semibold text-gray-200">
              You're in.
            </h1>
            <p className="text-xs text-gray-400">
              Redirecting you to the dashboard…
            </p>
          </>
        )}

        {status === 'error' && (
          <>
            <AlertTriangle className="mx-auto text-red-400" size={32} />
            <h1 className="text-lg font-semibold text-gray-200">
              Couldn't accept invite
            </h1>
            <p className="text-xs text-gray-400">{message}</p>
            <button
              onClick={() => navigate({ to: '/' })}
              className="mt-2 px-3 py-1.5 rounded bg-bg border border-bg-border
                         text-xs text-gray-300 hover:bg-bg-border transition"
            >
              Go home
            </button>
          </>
        )}
      </div>
    </div>
  )
}